import React, { useState, useEffect, useRef } from "react";
import { secureGet, securePut, secureDelete } from "../../config/api";
import { formatDateTime } from "../../utils/format";
import { useTranslation } from "react-i18next";
import Input from "../../components/Input";
import SnackbarNotification from "../../components/SnackbarNotification";

import CloseIcon from "../../icons/x.svg";
import ReplyIcon from "../../icons/reply.svg";
import DeleteIcon from "../../icons/trashR.svg";
import SendIcon from "../../icons/send.svg";
import chevron from "../../icons/chevron-downP.svg";

const Notes = ({ clientId, onClose }) => {
    const [notes, setNotes] = useState([]);
    const [replyStates, setReplyStates] = useState({});
    const [newComment, setNewComment] = useState("");
    const [sortBy, setSortBy] = useState("recent");
    const { t } = useTranslation();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const showSnackbar = (message, severity = 'success', manual = false) => {
        setSnackbar({ open: true, message, severity, manual });
    };

    useEffect(() => {
        const abortController = new AbortController();
        const fetchNotes = async () => {
            try {
                const response = await secureGet(`/clients/${clientId}/comments`, showSnackbar, { signal: abortController.signal });
                const sortedNotes = sortNotes(response.data, "recent");
                setNotes(sortedNotes);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error("Error fetching notes:", error);
                }
            }
        };

        fetchNotes();
        return () => {
            abortController.abort();
        };
    }, [clientId]);

    const sortNotes = (notesArray, sortOrder) => {
        return notesArray.sort((a, b) => {
            if (sortOrder === "recent") {
                return new Date(b.createdAt) - new Date(a.createdAt);
            } else {
                return new Date(a.createdAt) - new Date(b.createdAt);
            }
        });
    };

    const handleNewCommentSend = async () => {
        try {
            const payload = { clientId, comment: newComment, parentId: "0" };
            await securePut(`/clients/comments/add`, payload, showSnackbar);

            const updatedNotes = await secureGet(`/clients/${clientId}/comments`, showSnackbar);
            setNotes(sortNotes(updatedNotes.data, sortBy));

            setNewComment("");
        } catch (error) {
            console.error("Error sending comment:", error);
        }
    };

    const handleReply = (commentId) => {
        setReplyStates((prevState) => ({
            ...prevState,
            [commentId]: {
                isVisible: !prevState[commentId]?.isVisible,
                comment: prevState[commentId]?.comment || "",
            },
        }));
    };

    const handleReplyChange = (commentId, value) => {
        setReplyStates((prevState) => ({
            ...prevState,
            [commentId]: { ...prevState[commentId], comment: value },
        }));
    };

    const handleSendReply = async (commentId) => {
        try {
            const payload = { clientId, comment: replyStates[commentId]?.comment || "", parentId: commentId };
            await securePut(`/clients/comments/add`, payload, showSnackbar);

            const updatedNotes = await secureGet(`/clients/${clientId}/comments`, showSnackbar);
            setNotes(sortNotes(updatedNotes.data, sortBy));

            setReplyStates((prevState) => ({
                ...prevState,
                [commentId]: { isVisible: false, comment: "" },
            }));
        } catch (error) {
            console.error("Error sending reply:", error);
        }
    };

    const handleDelete = async (commentId) => {
        try {
            await secureDelete(`/clients/comments/delete/${commentId}`, showSnackbar);
            const updatedNotes = await secureGet(`/clients/${clientId}/comments`, showSnackbar);
            setNotes(sortNotes(updatedNotes.data, sortBy));
        } catch (error) {
            console.error("Error deleting note:", error);
        }
    };

    const renderReplies = (comments) => {
        return (
            <div className="ml-4 mt-4 space-y-4">
                {comments.map((comment) => (
                    <div key={comment.commentId} className="bg-gray-100 p-4 rounded-[12px] "
                    >
                        <div className="flex justify-between items-center">
                            <div>
                                <span className="text-[#6B78F8] text-[12px] font-semibold">
                                    {comment.firstName && comment.lastName
                                        ? `${comment.firstName} ${comment.lastName}`.toUpperCase()
                                        : ""}
                                </span>
                                <span className="text-[#707070] text-[12px] font-semibold ml-2">
                                    {comment.createdAt
                                        ? formatDateTime(comment.createdAt)
                                        : ""}
                                </span>
                                <p className="text-[#043F88] text-[14px] font-medium mt-2">
                                    {comment.content || ""}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    className="w-8 h-8 bg-[#F2F3F3] flex items-center justify-center rounded-[12px]"
                                    onClick={() => handleReply(comment.commentId)}
                                >
                                    <img src={ReplyIcon} alt="Reply" className="w-4 h-4" />
                                </button>
                                <button
                                    className="w-8 h-8 bg-[#F2F3F3] flex items-center justify-center rounded-[12px]"
                                    onClick={() => handleDelete(comment.commentId)}
                                >
                                    <img src={DeleteIcon} alt="Delete" className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Reply Input */}
                        {replyStates[comment.commentId]?.isVisible && (
                            <div className="flex items-center gap-4 mt-4">
                                <Input
                                    type="text"
                                    isNormal={true}
                                    placeholder={t(`placeholder.your_response`)}
                                    value={replyStates[comment.commentId]?.comment || ""}
                                    onChange={(value) => handleReplyChange(comment.commentId, value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && replyStates[comment.commentId]?.comment.trim()) {
                                            handleSendReply(comment.commentId);
                                        }
                                    }}
                                    className="flex-1 "
                                />

                                <button
                                    className={`w-10 h-10 flex items-center justify-center rounded-md ${replyStates[comment.commentId]?.comment.trim()
                                        ? "bg-[#043F88] cursor-pointer"
                                        : "bg-gray-300 cursor-not-allowed"
                                        }`}
                                    onClick={() => handleSendReply(comment.commentId)}
                                    disabled={!replyStates[comment.commentId]?.comment.trim()}
                                >
                                    <img src={SendIcon} alt="Send" />
                                </button>
                            </div>
                        )}

                        {/* Recursive rendering of child comments */}
                        {comment.comments && comment.comments.length > 0 && renderReplies(comment.comments)}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1050]">
            <div className="bg-white rounded-lg p-6 w-11/12 md:w-5/6 lg:w-3/4 xl:w-2/3 h-[90vh] flex flex-col  "  >

                <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-semibold text-black">Notes</h2>
                    <button
                        className="w-8 h-8 bg-[#FAFAFA] flex items-center justify-center rounded-full"
                        onClick={onClose}
                    >
                        <img src={CloseIcon} alt="Close" />
                    </button>
                </div>

                {/* Independent Comment Section */}
                <div className="flex items-center gap-4 mt-4">
                    <Input
                        type="text"
                        isNormal={true}
                        placeholder={t(`placeholder.add_comment`)}
                        value={newComment}
                        onChange={(value) => setNewComment(value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && newComment.trim()) {
                                handleNewCommentSend();
                            }
                        }}
                        className="flex-1 font-semibold"
                        width="80%"
                    />
                    <button
                        className={`w-10 h-10 bg-[#043F88] flex items-center justify-center rounded-md ${newComment.trim() ? "cursor-pointer" : "cursor-not-allowed"
                            }`}
                        onClick={handleNewCommentSend}
                        disabled={!newComment.trim()}
                    >
                        <img src={SendIcon} alt="Send" className="w-6 h-6" />
                    </button>
                </div>

                {/* Sort Section */}
                <div className="pt-4  flex justify-between items-center">
                    <h2 className="text-[#B0B7C3] text-[14px] font-semibold uppercase">Notes</h2>
                    <div className="flex items-center relative" ref={dropdownRef}>
                        <h3 className="text-[#043F88] text-[14px] font-semibold mr-2">
                            {t(`filters.products.sortBy`)}:
                        </h3>
                        <div className="relative">
                            <button
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                className="text-[#6B78F8] bg-transparent border-none text-[14px] font-semibold cursor-pointer flex items-center"
                            >
                                {sortBy === "recent"
                                    ? t(`placeholder.most_recent`)
                                    : t(`placeholder.less_recent`)}
                                <img
                                    src={chevron}
                                    alt="chevron"
                                    className={`ml-1 w-4 h-4 transition-transform duration-300 ${dropdownOpen ? "rotate-180" : ""}`}
                                />
                            </button>

                            {dropdownOpen && (
                                <div className="absolute mt-2 w-40 bg-white rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)] z-10">
                                    <ul className="py-2">
                                        <li
                                            className="px-4 py-2 text-[#707070] text-[14px] font-semibold hover:bg-gray-100 cursor-pointer"
                                            onClick={() => {
                                                setSortBy("recent");
                                                setNotes(sortNotes([...notes], "recent"));
                                                setDropdownOpen(false);
                                            }}
                                        >
                                            {t(`placeholder.most_recent`)}
                                        </li>
                                        <li
                                            className="px-4 py-2 text-[#707070] text-[14px] font-semibold hover:bg-gray-100 cursor-pointer"
                                            onClick={() => {
                                                setSortBy("oldest");
                                                setNotes(sortNotes([...notes], "oldest"));
                                                setDropdownOpen(false);
                                            }}
                                        >
                                            {t(`placeholder.less_recent`)}
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>

                </div>

                {/* Scrollable Notes List */}
                <div className="flex-1 overflow-y-auto mt-4 space-y-4">
                    {notes.map((note) => (
                        <div key={note.commentId} className="bg-white p-4 rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)]"
                        >
                            <div className="flex justify-between items-center">
                                <div>
                                    <span className="text-[#6B78F8] text-[12px] font-semibold">
                                        {note.firstName && note.lastName ? `${note.firstName} ${note.lastName}`.toUpperCase() : "Auteur inconnu"}
                                    </span>
                                    <span className="text-[#707070] text-[12px] font-semibold ml-2">
                                        {note.createdAt ? formatDateTime(note.createdAt) : "Date inconnue"}
                                    </span>
                                    <p className="text-[#043F88] text-[14px] font-medium mt-2">
                                        {note.content || ""}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        className="w-8 h-8 bg-[#F2F3F3] flex items-center justify-center rounded-[12px]"
                                        onClick={() => handleReply(note.commentId)}
                                    >
                                        <img src={ReplyIcon} alt="Reply" className="w-4 h-4" />
                                    </button>
                                    <button
                                        className="w-8 h-8 bg-[#F2F3F3] flex items-center justify-center rounded-[12px]"
                                        onClick={() => handleDelete(note.commentId)}
                                    >
                                        <img src={DeleteIcon} alt="Delete" className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Reply Input */}
                            {replyStates[note.commentId]?.isVisible && (
                                <div className="flex items-center gap-4 mt-4">
                                    <Input
                                        type="text"
                                        placeholder={t(`placeholder.your_response`)}
                                        value={replyStates[note.commentId]?.comment || ""}
                                        onChange={(value) => handleReplyChange(note.commentId, value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && replyStates[note.commentId]?.comment.trim()) {
                                                handleSendReply(note.commentId);
                                            }
                                        }}
                                        isNormal={true}
                                        className="flex-1 border border-gray-300 rounded-md px-4 py-2"
                                    />
                                    <button
                                        className={`w-10 h-10 flex items-center justify-center rounded-md ${replyStates[note.commentId]?.comment.trim() ? "bg-[#043F88] cursor-pointer" : "bg-gray-300 cursor-not-allowed"
                                            }`}
                                        onClick={() => handleSendReply(note.commentId)}
                                        disabled={!replyStates[note.commentId]?.comment.trim()}
                                    >
                                        <img src={SendIcon} alt="Send" />
                                    </button>
                                </div>
                            )}

                            {/* Recursive rendering of child comments */}
                            {note.comments && note.comments.length > 0 && renderReplies(note.comments)}
                        </div>
                    ))}
                </div>
            </div>
            <SnackbarNotification
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                manual={snackbar.manual}
            />

        </div>

    );
};

export default Notes;
